import { Button, TextField } from '@mui/material';
import { DashboardMutatePayload } from '@thingslog/repositories';
import React, { FC } from 'react';
import { Controller, FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TimespanPickerFields, TimespanPickerMode } from '../timespan';

interface Props {
  type: 'create' | 'edit';
  defaultValues?: Partial<DashboardMutatePayload>;
  disabled?: boolean;
  onSubmit: (payload: DashboardMutatePayload) => void;
  onCancel: () => void;
}

const createDefaultCustomPeriod = (): DashboardMutatePayload['period'] => {
  const today = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(today.getMonth() - 1);

  return {
    type: 'custom',
    fromDate: oneMonthAgo.toISOString().split('T')[0],
    toDate: today.toISOString().split('T')[0],
  };
};

const defaultDashboardLayoutSettings = {
  colsDesktop: 12,
  colsMobile: 4,
  layoutSwitchBreakpointPx: 768,
};

const getDashboardFormDefaultValues = (
  defaultValues?: Partial<DashboardMutatePayload>
): DashboardMutatePayload => {
  return {
    name: '',
    period: { type: 'predefined', name: 'this-month' },
    ...defaultDashboardLayoutSettings,
    ...defaultValues,
  };
};

const buildDashboardMutatePayload = (
  payload: DashboardMutatePayload,
  defaultValues: DashboardMutatePayload
): DashboardMutatePayload => ({
  name: payload.name,
  period: payload.period,
  colsDesktop: defaultValues.colsDesktop,
  colsMobile: defaultValues.colsMobile,
  layoutSwitchBreakpointPx: defaultValues.layoutSwitchBreakpointPx,
});

export const DashboardSettingsForm: FC<Props> = ({
  type,
  defaultValues,
  disabled = false,
  onSubmit,
  onCancel,
}: Props) => {
  const { t } = useTranslation();
  const dashboardFormDefaultValues = getDashboardFormDefaultValues(defaultValues);
  const { control, handleSubmit, setValue, watch } = useForm<DashboardMutatePayload>({
    mode: 'onChange',
    defaultValues: dashboardFormDefaultValues,
  });

  const periodType = watch('period.type');
  const submitButtonText =
    type === 'create'
      ? t('dashboard_create_dashboard', 'Create Dashboard')
      : t('dashboard_save_dashboard', 'Save Dashboard');

  const handlePeriodTypeChange = (nextType: TimespanPickerMode): void => {
    if (nextType === 'none') {
      return;
    }

    const nextPeriod: DashboardMutatePayload['period'] =
      nextType === 'predefined'
        ? { type: 'predefined', name: 'this-month' }
        : createDefaultCustomPeriod();

    setValue('period', nextPeriod, { shouldDirty: true, shouldValidate: true });
    setValue('period.type', nextType, { shouldDirty: true, shouldValidate: true });
  };

  return (
    <form
      className="flex min-w-0 flex-col gap-4 p-4"
      onSubmit={handleSubmit((payload: DashboardMutatePayload): void =>
        onSubmit(buildDashboardMutatePayload(payload, dashboardFormDefaultValues))
      )}
    >
      <div data-dashboard-tour="dashboard-form-name">
        <Controller
          name="name"
          control={control}
          rules={{
            required: t('dashboard_name_required', 'Dashboard name is required'),
            maxLength: {
              value: 100,
              message: t(
                'dashboard_name_max_length',
                'Dashboard name must be less than 100 characters'
              ),
            },
            validate: (value: string): string | true =>
              value.trim().length > 0
                ? true
                : t('dashboard_name_required', 'Dashboard name is required'),
          }}
          render={({ field, fieldState }: FieldValues): JSX.Element => (
            <TextField
              {...field}
              label={t('dashboard_name', 'Dashboard Name')}
              size="small"
              fullWidth
              disabled={disabled}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </div>

      <div data-dashboard-tour="dashboard-form-period">
        <TimespanPickerFields<DashboardMutatePayload>
          control={control}
          mode={periodType}
          modeName="period.type"
          title={t('dashboard_time_period', 'Time Period')}
          predefinedName="period.name"
          fromDateName="period.fromDate"
          toDateName="period.toDate"
          disabled={disabled}
          onModeChange={handlePeriodTypeChange}
        />
      </div>

      <div className="flex justify-end gap-2 border-0 border-t border-solid border-gray-200 pt-4">
        <Button variant="outlined" onClick={onCancel} disabled={disabled}>
          {t('cancel', 'Cancel')}
        </Button>
        <Button type="submit" variant="contained" disableElevation disabled={disabled}>
          {submitButtonText}
        </Button>
      </div>
    </form>
  );
};
